import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import CustomerAvatar from "../components/CustomerAvatar";
import useUrlState, { param, useScrollRestoration, useValidOption } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { fetchBranches } from "../services/branchService";
import { fetchBranchReviews } from "../services/reviewService";
import { formatDate } from "../services/datetime";

const STARS = [1, 2, 3, 4, 5];

function Stars({ value, label }) {
  const rounded = Math.round(Number(value) || 0);
  return (
    <span className="inline-flex items-center gap-0.5" role="img" aria-label={label}>
      {STARS.map((star) => (
        <svg key={star} viewBox="0 0 20 20" className="h-3.5 w-3.5" aria-hidden="true">
          <path
            d="M10 1.8l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.4l-4.94 2.6.94-5.5-4-3.9 5.53-.8L10 1.8z"
            className={star <= rounded ? "fill-warning stroke-warning" : "fill-transparent stroke-line-strong"}
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </span>
  );
}

/** Enlarged photo; closes on the backdrop, the button or Escape. */
function PhotoViewer({ url, onClose }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!url) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, onClose]);

  if (!url) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div role="dialog" aria-modal="true" className="relative">
        <img src={url} alt="" className="max-h-[85vh] max-w-full rounded-lg" />
        <button type="button" className="btn-secondary absolute end-2 top-2" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    </div>
  );
}

const FILTERS = {
  branch: param.string(),
  provider: param.string(),
  rating: param.oneOf(["1", "2", "3", "4", "5"]),
  page: param.int(1),
};

/**
 * Reviews, per branch, newest first. Read-only.
 *
 * Gated by `reviews.view` in the router and the sidebar; the API checks the
 * permission and the branch again on every request. The branch list is the
 * API's own — only the branches this user can reach.
 */
export default function Reviews() {
  const { t, i18n } = useTranslation();
  // Filters and page live in the URL (see useUrlState); `review` — the
  // focus a notification adds — is read directly and left alone.
  const { values, set: setParam } = useUrlState(FILTERS);
  const [params] = useSearchParams();

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null);

  const branchId = values.branch || branches[0]?.id || "";
  const staffId = values.provider;
  const rating = values.rating;
  const page = values.page;
  // A review notification opens the page focused on its review.
  const focusId = params.get("review") || "";
  const focused = useRef(null);

  useEffect(() => {
    focused.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [reviews]);

  // A branch id this user cannot reach falls back to their first branch.
  useValidOption(values.branch, branches.length ? branches : null, () => setParam({ branch: "" }));
  useScrollRestoration(!loading);

  useEffect(() => {
    (async () => {
      try {
        const { branches: list } = await fetchBranches(1, 100);
        setBranches(list);
      } catch (err) {
        setError(apiErrorMessage(err, t("reviews.branchesFailed")));
      } finally {
        setBranchesLoading(false);
      }
    })();
  }, [t]);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError("");
    try {
      const result = await fetchBranchReviews(branchId, { page, staffId, rating });
      setReviews(result.reviews);
      setMeta(result.meta);
    } catch (err) {
      setError(apiErrorMessage(err, t("reviews.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [branchId, page, staffId, rating, t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  const locale = i18n.language === "ar" ? "ar" : "en";
  const number = (value, digits = 0) =>
    value == null
      ? "—"
      : new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
  const date = (iso) => formatDate(iso, locale);
  const providers = meta?.providers ?? [];

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold">{t("reviews.title")}</h1>
        <p className="text-sm text-ink-2">{t("reviews.subtitle")}</p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {branchesLoading ? (
        <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>
      ) : branches.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-sm text-ink-2">{t("reviews.noBranches")}</p>
        </div>
      ) : (
        <>
          <div className="card mb-4 grid gap-3 p-4 sm:grid-cols-3">
            <div>
              <label className="field-label" htmlFor="rv-branch">
                {t("reviews.branch")}
              </label>
              <select
                id="rv-branch"
                className="field-input"
                value={branchId}
                onChange={(e) => setParam({ branch: e.target.value, provider: "", rating: "" })}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="rv-provider">
                {t("reviews.provider")}
              </label>
              <select
                id="rv-provider"
                className="field-input"
                value={staffId}
                onChange={(e) => setParam({ provider: e.target.value })}
              >
                <option value="">{t("reviews.allProviders")}</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="rv-rating">
                {t("reviews.serviceRating")}
              </label>
              <select
                id="rv-rating"
                className="field-input"
                value={rating}
                onChange={(e) => setParam({ rating: e.target.value })}
              >
                <option value="">{t("reviews.anyRating")}</option>
                {[5, 4, 3, 2, 1].map((star) => (
                  <option key={star} value={star}>
                    {t("reviews.stars", { count: star, formatted: number(star) })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {meta && (
            <div className="mb-4 grid gap-3 lg:grid-cols-[14rem_1fr]">
              <div className="card p-4">
                <p className="text-caption text-ink-2">{t("reviews.branchAverage")}</p>
                <p className="mt-1 text-2xl font-bold">{number(meta.rating?.average, 1)}</p>
                <Stars
                  value={meta.rating?.average}
                  label={t("reviews.outOf", { value: number(meta.rating?.average, 1) })}
                />
                <p className="mt-1 text-xs text-ink-2">
                  {t("reviews.count", { count: meta.rating?.count ?? 0, formatted: number(meta.rating?.count ?? 0) })}
                </p>
              </div>
              <div className="card p-4">
                <p className="mb-2 text-caption text-ink-2">{t("reviews.providerAverages")}</p>
                {providers.length === 0 ? (
                  <p className="text-sm text-ink-2">{t("reviews.noProviderRatings")}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {providers.map((provider) => (
                      <span
                        key={provider.id}
                        className="inline-flex items-center gap-2 rounded-full border border-line px-3 py-1 text-sm"
                      >
                        <span className="font-semibold">{provider.display_name}</span>
                        <span>{number(provider.average, 1)}</span>
                        <span className="text-xs text-ink-2">({number(provider.count)})</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>
          ) : reviews.length === 0 ? (
            <div className="card p-8 text-center">
              <p className="text-sm text-ink-2">{t("reviews.empty")}</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {reviews.map((review) => (
                <li
                  key={review.id}
                  ref={review.id === focusId ? focused : undefined}
                  className={`card p-4 ${review.is_hidden ? "opacity-60" : ""} ${review.id === focusId ? "ring-2 ring-accent" : ""}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      <CustomerAvatar name={review.customer?.name ?? ""} photoUrl={review.customer?.photo_url} />
                      <div className="min-w-0">
                      <p className="font-semibold">{review.service?.name ?? "—"}</p>
                      <p className="text-xs text-ink-2">
                        {[review.customer?.name || t("reviews.customer"), date(review.created_at)].join(" · ")}
                      </p>
                      </div>
                    </div>
                    {review.is_hidden && (
                      <span className="rounded-full border border-danger-line bg-danger-wash px-2 py-0.5 text-xs text-danger-text">
                        {t("reviews.hiddenByAdmin")}
                      </span>
                    )}
                  </div>

                  <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <dt className="text-ink-2">{t("reviews.service")}</dt>
                      <dd>
                        <Stars
                          value={review.service_rating}
                          label={t("reviews.outOf", { value: number(review.service_rating) })}
                        />
                      </dd>
                    </div>
                    {review.provider && (
                      <div className="flex items-center gap-2">
                        <dt className="text-ink-2">{review.provider.display_name}</dt>
                        <dd>
                          {review.provider_rating ? (
                            <Stars
                              value={review.provider_rating}
                              label={t("reviews.outOf", { value: number(review.provider_rating) })}
                            />
                          ) : (
                            "—"
                          )}
                        </dd>
                      </div>
                    )}
                  </dl>

                  {review.comment && (
                    <p
                      lang={review.locale}
                      dir={review.locale === "ar" ? "rtl" : "ltr"}
                      className="mt-2 whitespace-pre-line text-sm"
                    >
                      {review.comment}
                    </p>
                  )}

                  {review.photos?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {review.photos.map((photo) => (
                        <button
                          key={photo.id}
                          type="button"
                          onClick={() => setViewing(photo.url)}
                          aria-label={t("reviews.enlargePhoto")}
                        >
                          <img src={photo.url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                        </button>
                      ))}
                    </div>
                  )}

                  {review.is_hidden && review.hidden_reason && (
                    <p className="mt-2 text-xs text-ink-2">{t("reviews.hiddenReason", { reason: review.hidden_reason })}</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {meta && meta.last_page > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={page <= 1}
                onClick={() => setParam({ page: page - 1 })}
              >
                {t("common.previous")}
              </button>
              <span className="text-sm text-ink-2">
                {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
              </span>
              <button
                type="button"
                className="btn-secondary"
                disabled={page >= meta.last_page}
                onClick={() => setParam({ page: page + 1 })}
              >
                {t("common.next")}
              </button>
            </div>
          )}
        </>
      )}

      <PhotoViewer url={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
